import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Archive, Search, RotateCcw, Package, Car, User, Calendar, Filter,
  ChevronRight,
} from "lucide-react";

const JOB_TYPE_STATUSES = {
  mechanic: ["entrou", "em_inspecao", "aguardando_aprovacao", "em_execucao", "quality_check", "pronto_entrega", "entregue"],
  warranty: ["entrou", "estimate", "kit_recebido", "aguardando_aprovacao", "aprovado", "em_execucao", "pronto", "entregue", "aguardando_pagamento"],
  body_shop: ["novo", "estimate", "aguardando_aprovacao", "aprovado", "em_execucao", "quality_check", "pronto_entrega", "entregue"],
};

export default function Arquivados() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    q: "",
    technician: "",
    status: "",
    job_type: "",
    delivered_from: "",
    delivered_to: "",
  });
  const [restoreState, setRestoreState] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState("");
  const [restoring, setRestoring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/jobs/archived", { params });
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar arquivados");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const clearFilters = () => {
    setFilters({ q: "", technician: "", status: "", job_type: "", delivered_from: "", delivered_to: "" });
    setTimeout(load, 0);
  };

  const validStatuses = restoreState ? (JOB_TYPE_STATUSES[restoreState.job_type] || JOB_TYPE_STATUSES.mechanic) : [];

  const openRestore = (job) => {
    setRestoreState(job);
    setRestoreTarget(JOB_TYPE_STATUSES[job.job_type]?.[0] || "entrou");
  };

  const submitRestore = async () => {
    setRestoring(true);
    try {
      await api.post(`/jobs/${restoreState.id}/restore`, { target_status: restoreTarget });
      toast.success("Veículo restaurado ao Kanban");
      setRestoreState(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao restaurar");
    } finally { setRestoring(false); }
  };

  const total = items.length;

  return (
    <div className="space-y-6" data-testid="arquivados-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Archive className="w-3.5 h-3.5" /> Arquivados
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">
            Histórico de veículos finalizados
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Todo veículo entregue é arquivado (manual ou automaticamente aos domingos 23:59 UTC). Histórico completo preservado.
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold font-mono">{total}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Registros</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="border border-border rounded-md bg-card p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-3">
          <Filter className="w-3 h-3" /> Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <Label className="text-[10px]">Busca geral</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && load()}
                className="pl-7"
                placeholder="Cliente · placa · VIN · marca · modelo"
                data-testid="arch-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px]">Técnico</Label>
            <Input value={filters.technician} onChange={(e) => setFilters({ ...filters, technician: e.target.value })} data-testid="arch-tech" />
          </div>
          <div>
            <Label className="text-[10px]">Kanban</Label>
            <Select value={filters.job_type || "all"} onValueChange={(v) => setFilters({ ...filters, job_type: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="arch-jobtype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="mechanic">Oficina</SelectItem>
                <SelectItem value="body_shop">Body Shop</SelectItem>
                <SelectItem value="warranty">Garantia Estendida</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px]">Entregue desde</Label>
            <Input type="date" value={filters.delivered_from} onChange={(e) => setFilters({ ...filters, delivered_from: e.target.value })} data-testid="arch-from" />
          </div>
          <div>
            <Label className="text-[10px]">Entregue até</Label>
            <Input type="date" value={filters.delivered_to} onChange={(e) => setFilters({ ...filters, delivered_to: e.target.value })} data-testid="arch-to" />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={load} data-testid="arch-apply"><Search className="w-3.5 h-3.5 mr-1.5" /> Aplicar</Button>
          <Button size="sm" variant="outline" onClick={clearFilters} data-testid="arch-clear">Limpar</Button>
        </div>
      </div>

      {/* Lista */}
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 bg-secondary/60 text-[10px] uppercase tracking-widest text-muted-foreground">
          <div className="col-span-3">Veículo</div>
          <div className="col-span-2">Cliente</div>
          <div className="col-span-2">Placa · VIN</div>
          <div className="col-span-1">Kanban</div>
          <div className="col-span-2">Entregue / Arquivado</div>
          <div className="col-span-1">Técnico</div>
          <div className="col-span-1 text-right">Ações</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Carregando…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-40" />
            <div className="text-sm text-muted-foreground">Nenhum registro arquivado com esses filtros.</div>
          </div>
        ) : items.map((j) => {
          const del = j.delivered_at ? new Date(j.delivered_at).toLocaleDateString("pt-BR") : "—";
          const arch = j.archived_at ? new Date(j.archived_at).toLocaleDateString("pt-BR") : "—";
          return (
            <div key={j.id} className="md:grid md:grid-cols-12 md:gap-3 flex flex-col px-4 py-3 border-t border-border md:items-center" data-testid={`arch-row-${j.id}`}>
              <button
                onClick={() => j.vehicle?.id && navigate(`/veiculos/${j.vehicle.id}`)}
                className="md:col-span-3 flex items-center gap-2.5 text-left hover:text-primary transition"
              >
                <div className="w-10 h-10 rounded bg-secondary shrink-0 overflow-hidden">
                  {j.vehicle?.photo_url ? <img src={j.vehicle.photo_url} alt="" className="w-full h-full object-cover" /> :
                    <div className="w-full h-full flex items-center justify-center"><Car className="w-4 h-4 text-muted-foreground" /></div>}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{j.vehicle?.year} {j.vehicle?.make} {j.vehicle?.model}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{j.service_type}</div>
                </div>
              </button>
              <div className="md:col-span-2 flex items-center gap-1.5 text-sm min-w-0">
                <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="truncate">{j.client_name || "—"}</span>
              </div>
              <div className="md:col-span-2 font-mono text-xs">
                <div>{j.vehicle?.plate || "—"}</div>
                <div className="text-muted-foreground">{j.vehicle?.vin ? j.vehicle.vin.slice(-8) : "—"}</div>
              </div>
              <div className="md:col-span-1 text-[10px] uppercase tracking-widest">
                {{ mechanic: "Oficina", body_shop: "Body", warranty: "Garantia" }[j.job_type] || j.job_type}
              </div>
              <div className="md:col-span-2 text-xs">
                <div className="flex items-center gap-1.5"><Package className="w-3 h-3 text-muted-foreground" /> {del}</div>
                <div className="flex items-center gap-1.5 text-muted-foreground"><Archive className="w-3 h-3" /> {arch}</div>
              </div>
              <div className="md:col-span-1 text-xs font-mono truncate">{j.technician || "—"}</div>
              <div className="md:col-span-1 flex justify-end gap-1.5">
                <Button size="sm" variant="outline" onClick={() => openRestore(j)} data-testid={`arch-restore-${j.id}`}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restaurar
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Restaurar dialog */}
      <Dialog open={!!restoreState} onOpenChange={(v) => !v && setRestoreState(null)}>
        <DialogContent data-testid="arch-restore-dialog">
          <DialogHeader>
            <DialogTitle>Restaurar veículo ao Kanban</DialogTitle>
            <DialogDescription>
              O veículo <b>{restoreState?.vehicle?.year} {restoreState?.vehicle?.make} {restoreState?.vehicle?.model}</b> voltará ao Kanban. Todo o histórico permanece intacto e a data de restauração será registrada.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Coluna de destino no Kanban</Label>
            <Select value={restoreTarget} onValueChange={setRestoreTarget}>
              <SelectTrigger data-testid="arch-restore-target"><SelectValue /></SelectTrigger>
              <SelectContent>
                {validStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRestoreState(null)}>Cancelar</Button>
            <Button onClick={submitRestore} disabled={restoring || !restoreTarget} data-testid="arch-restore-confirm">
              <RotateCcw className="w-4 h-4 mr-1.5" /> {restoring ? "Restaurando…" : "Restaurar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
